<template>
  <v-data-table
    dense
    :items="groupedRows"
    :headers="headerList"
    :items-per-page="-1"
    hide-default-footer
    class="elevation-1 war-appr-table"
  >
    <template v-slot:top>
      <div class="d-flex justify-end">
        <v-btn
          class="mx-2" color="primary"
          fab icon x-small
          v-if="isMultipleFunds"
          @click="addOne"
        >
          <v-icon>{{ icons.mdiPlus }}</v-icon>
        </v-btn>
      </div>
    </template>

    <template v-slot:item="{ item, index }">
      <tr   v-if="item.isFundTotal && TransactionEditedItem.type === 'APPROPRIATIONS'"
      class="text-caption blue--text">
        <td colspan="4" class="text-right pr-0">
          <strong>Fund {{ fundDescNumber(item.fund) }}
          Total:</strong> ${{ amountFormat(item.total) }}
        </td>
      </tr>
      <tr v-else>
        <td>
          <v-autocomplete
            v-model="item.fund"
            :items="funds"
            :rules="reqdSelectValidation"
            item-text="fundDesc"
            item-value="_id"
            dense
            single-line
            class="war-trans-fund"
            :id="`war-trans-fund-${editedItem.indexOf(item)}`"
            @blur="somethingChanged"
            @change="somethingChanged(); onChangeFund();
             checkAndUpdateAccountBalance(item);
              updateStatsBalance(item, warStatsBalanceParams().fundBalance);"
          ></v-autocomplete>
        </td>
        <td v-if="!isFundsOnly">
          <v-autocomplete
            v-model="item.department"
            :items="departments"
            item-text="deptDesc"
            item-value="_id"
            dense
            single-line
            :id="`war-trans-dept-${editedItem.indexOf(item)}`"
            @blur="somethingChanged"
          ></v-autocomplete>
        </td>
        <td v-if="!isFundsOnly">
          <v-autocomplete
            v-model="item.account"
            :items="accounts"
            item-text="accDesc"
            item-value="_id"
            dense
            single-line
            :id="`war-trans-acc-${editedItem.indexOf(item)}`"
            @blur="somethingChanged"
            @change="updateStatsBalance(item, warStatsBalanceParams().accountBalance);
             checkAndUpdateAccountBalance(item);"
          ></v-autocomplete>
        </td>
        <td>
          <v-text-field
            v-model="item.amount"
            :rules="reqdSelectValidation"
            dense
            single-line
            class="trans-amount"
            :id="`war-trans-amt-${editedItem.indexOf(item)}`"
            @blur="somethingChanged"
            @keydown="shortcutHandler($event, item, index)"
            @focus="focusAmount"
            @keydown.enter="somethingChanged"
          ></v-text-field>
        </td>
        <td>
          <v-icon
            small
            class="mr-2 war-trans-item-delete"
            tabindex="-1"
            @click="removeOne(item)"
          >
            {{ icons.mdiDelete }}
          </v-icon>
        </td>
      </tr>
    </template>

    <!-- Overall Total -->
    <template slot="body.append">
      <tr class="blue--text text-caption">
        <td colspan="2" id="total-trans-amount">
          Total : ${{ amountFormat(sumAmount) }}
        </td>
      </tr>
    </template>
  </v-data-table>
</template>

<script>
import {
  mapGetters, mapState, mapActions, mapMutations,
} from 'vuex';
import {
  mdiPlus,
  mdiDelete,
} from '@mdi/js';
import {
  focusAmount,
  amountFormat,
  amountToNumber,
  isTabOrPlus,
  checkStringEmptiness,
  extractId,
} from '../../../util/shared/vue-global';
import { warStatsBalanceParams } from './warTransaction.util';
import { isBudgetedFundOrCashFund } from '../../shared/shared.util';

const defaultItem = {
  fund: null,
  department: null,
  account: null,
  amount: 0,
};
const departmentCrudKey = 'warDepartment';
const accountCrudKey = 'warAccount';

export default {
  name: 'WarApprDetails',
  props: {
    baseCrudKey: {
      type: String,
      default: 'war-dialog',
    },
    value: {
      type: Array,
      default: () => [],
    },
    activeTransItem: {
      type: Object,
      default: () => {},
    },
  },
  created() {
    this.loadFundAndAccBal();
  },
  beforeDestroy() {
    if (this.warrantRegPolling) {
      clearInterval(this.warrantRegPolling);
      this.setWarrantRegPolling(undefined);
    }
  },
  watch: {
    fundLabel() {
      this.headers = this.headers.map((h) => {
        if (h.name === 'fundCol') {
          return {
            ...h,
            text: this.fundLabel,
          };
        }
        return h;
      });
    },
    accountLabel() {
      this.headers = this.headers.map((h) => {
        if (h.name === 'accCol') {
          return {
            ...h,
            text: this.accountLabel,
          };
        }
        return h;
      });
    },
    isVisibleProp() {
      if (this.isVisibleProp) {
        this.loadFundAndAccBal();
      }
    },
  },
  computed: {
    ...mapGetters('base/crud', ['criteria', 'item', 'items', 'index', 'isVisible']),
    ...mapState({
      warrantRegPolling: (state) => state.war.transaction.warrantRegPolling,
      nextPaymentNumber: (state) => state.war.transaction.nextPaymentNumber,
      nextWarrantNumber: (state) => state.war.transaction.nextWarrantNumber,
      warFundBalance: (state) => state.war.transaction.warFundBalance,
      warAccountBalance: (state) => state.war.transaction.warAccountBalance,
      transactionTypes: (state) => state.enums.WarTransTypeEnum,
      warTransStatus: (state) => state.enums.WarTransStatusEnum,
      warSettingItem: (state) => state.SystemConfig.warSettingItem,
      fundListByFy: (state) => state.war.transaction.fundListByFy,
    }),
    reqdSelectValidation() {
      return [
        (value) => !!value || 'Required',
      ];
    },
    editedItem: {
      get() {
        const newVal = JSON.parse(JSON.stringify(this.value));
        const updatedValue = newVal.map(this.loadInitialValues);

        return updatedValue;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    TransactionEditedItem: {
      get() {
        return this.item(this.baseCrudKey);
      },
      set(value) {
        this.setItem([this.baseCrudKey, value]);
      },
    },
    amountValidation() {
      return [
        (value) => !!value || 'Required',
      ];
    },
    groupedRows() {
      const result = [];
      let currentFund = null;
      let currentGroup = [];
      let currentSubtotal = 0;
      const nullFundItems = [];

      this.editedItem.forEach((item) => {
        const fund = this.extractFundId(item.fund);
        const amount = this.amountToNumber(item.amount) || 0;

        if (item.fund == null) {
          nullFundItems.push(item);
          return;
        }

        if (currentFund !== null && fund !== currentFund) {
          result.push(...currentGroup);
          if (this.TransactionEditedItem.type === 'APPROPRIATIONS' && this.index(this.baseCrudKey) === -1 && this.isMultipleAppropriationEntry) {
            result.push({
              isFundTotal: true,
              fund: currentFund,
              total: currentSubtotal,
            });
          }
          currentGroup = [];
          currentSubtotal = 0;
        }

        currentGroup.push(item);
        currentFund = fund;
        currentSubtotal += amount;
      });

      if (currentGroup.length > 0 && currentFund) {
        result.push(...currentGroup);
        if (this.TransactionEditedItem.type === 'APPROPRIATIONS' && this.index(this.baseCrudKey) === -1 && this.isMultipleAppropriationEntry) {
          result.push({
            isFundTotal: true,
            fund: currentFund,
            total: currentSubtotal,
          });
        }
      }
      result.push(...nullFundItems);
      return result;
    },

    sumAmount() {
      let totalSum = 0.00;
      for (let i = 0; i < this.editedItem.length; i += 1) {
        totalSum += amountToNumber(this.editedItem[i].amount);
      }
      this.$emit('latestAmount', totalSum);
      return totalSum;
    },
    funds() {
      const fundObjs = this.fundListByFy || [];
      const updatedFundObj = fundObjs.filter((f) => isBudgetedFundOrCashFund(f.type))
        .map((f) => ({
          ...f,
          fundDesc: `${f.fund} ${f.description || ''}`,
        }));
      return [{ _id: null, fund: '', fundDesc: '' }, ...updatedFundObj];
    },
    departments() {
      const departmentObjs = this.items(departmentCrudKey) || [];
      const updatedDeptObj = departmentObjs.map((d) => ({
        ...d,
        deptDesc: `${d.dept} ${d.description || ''}`,
      }));
      return [{ _id: null, dept: '', deptDesc: '' }, ...updatedDeptObj];
    },
    accounts() {
      const accountObjs = this.items(accountCrudKey) || [];
      const updatedAccObjs = accountObjs.map((a) => ({
        ...a,
        accDesc: `${a.account} ${a.shortDesc || ''}`,
      }));
      return [{ _id: null, account: '', accDesc: '' }, ...updatedAccObjs];
    },
    isFundsOnly() {
      return this.warSettingItem && this.warSettingItem.fundsOnly;
    },
    isMultipleAppropriationEntry() {
      return this.warSettingItem && this.warSettingItem.multipleAppropriationEntry;
    },
    headerList() {
      if (this.isFundsOnly) {
        const updatedHeaders = (this.headers || []).filter((h) => h.value !== 'department' && h.value !== 'account');
        return updatedHeaders;
      }
      return this.headers;
    },
    isMultipleFunds() {
      return this.warSettingItem && this.warSettingItem.multipleFunds;
    },
    fundLabel() {
      const balance = this.warFundBalance || this.warFundBalance === 0 ? amountFormat(this.warFundBalance) : '...';
      return `Fund (bal before: $${balance})`;
    },
    accountLabel() {
      const balance = this.warAccountBalance || this.warFundBalance === 0 ? amountFormat(this.warAccountBalance) : '...';
      return `Account (bal before: $${balance})`;
    },
    isVisibleProp() {
      return this.isVisible(this.baseCrudKey);
    },
  },
  data: () => ({
    icons: {
      mdiPlus,
      mdiDelete,
    },
    fundLastSearched: undefined,
    headers: [
      { name: 'fundCol', text: 'Fund', value: 'fund' },
      { name: 'deptCol', text: 'Department', value: 'department' },
      { name: 'accCol', text: 'Account:', value: 'account' },
      { name: 'amtCol', text: 'Amount', value: 'amount' },
      {
        name: 'actCol', text: 'Actions', value: 'actions', sortable: false,
      },
    ],
  }),
  methods: {
    amountFormat,
    amountToNumber,
    focusAmount,
    warStatsBalanceParams,
    loadInitialValues(item) {
      return {
        ...item,
        amount: amountFormat(amountToNumber(item.amount)),
      };
    },
    ...mapActions('SystemConfig', ['loadConfig']),
    ...mapActions('war/transaction', [
      'loadLastWarrantNo',
      'loadLastPaymentNo',
      'warStatsBalance',
    ]),
    ...mapMutations('war/transaction', [
      'setWarrantRegPolling',
    ]),
    determineNextWarrantNo({ fund, fiscalYear }) {
      const loadWarNoPromise = this.loadLastWarrantNo(
        { fund, fiscalYear, baseCrudKey: this.baseCrudKey },
      );
      loadWarNoPromise.then(() => {
        this.$emit('warNo', this.nextWarrantNumber);
      });
    },
    extractFundId(fund) {
      if (!fund) return '';
      return typeof fund === 'string' ? fund.split(' ')[0] : fund._id || fund;
    },
    determineNextPaymentNo({ fund, fiscalYear }) {
      const loadPaymentNoPromise = this.loadLastPaymentNo(
        { fund, fiscalYear, baseCrudKey: this.baseCrudKey },
      );
      loadPaymentNoPromise.then(() => {
        this.$emit('paymentNo', this.nextPaymentNumber);
      });
    },
    checkAndUpdateAccountBalance(item) {
      if (item.fund && item.department && item.account) {
        this.updateStatsBalance(item); // No balanceType
      }
    },
    somethingChanged() {
      this.$emit('input', this.editedItem);
    },
    shortcutHandler(e, item) {
      if (this.isMultipleFunds && isTabOrPlus(e)) {
        const actualIndex = this.editedItem.findIndex((i) => i === item);
        const isLastRow = actualIndex === this.editedItem.length - 1;

        if (isLastRow) {
          this.addOne();
        }
      }
    },
    addOne() {
      const newItem = { ...defaultItem };
      if (this.TransactionEditedItem.type === 'APPROPRIATIONS' && this.index(this.baseCrudKey) === -1 && this.isMultipleAppropriationEntry) {
        // Get the last item to autopopulate the fund and department
        // This implements the requirement to autopopulate the fund number on the second line
        const lastItem = this.editedItem[this.editedItem.length - 1] || {};
        newItem.fund = lastItem.fund || null;
        newItem.department = lastItem.department || null;
        newItem.account = lastItem.account || null;
      }

      this.$emit('input', [...this.editedItem, newItem]);
      if (this.TransactionEditedItem.type === 'APPROPRIATIONS' && this.index(this.baseCrudKey) === -1 && this.isMultipleAppropriationEntry) {
        this.focusNewRow();
      }
      return true;
    },

    focusNewRow() {
      setTimeout(() => {
        const newRowIndex = this.editedItem.length - 1;
        const fundField = document.getElementById(`war-trans-fund-${newRowIndex}`);
        if (fundField) {
          fundField.focus();
        }
      }, 100);
    },
    fundDescNumber(fundId) {
      if (!fundId) return '';
      const fund = this.funds.find((f) => f._id === fundId);
      return fund ? fund.fund || '' : '';
    },
    removeOne(item) {
      this.$emit('input', [
        ...this.editedItem.filter((candidate) => item !== candidate),
      ]);
    },
    determineNxtTransNos() {
      if (this.activeTransItem.type === 'WARRANTS') {
        this.setWarrantRegPolling(undefined);
        const warrantRegPolling = setInterval(
          this.fetchTransNo,
          1200,
        );
        this.setWarrantRegPolling(warrantRegPolling);
      }
    },
    fetchTransNo() {
      const { fiscalYear } = this.activeTransItem;
      if (Array.isArray(this.editedItem) && this.editedItem.length > 0) {
        const fundId = extractId(this.editedItem[0].fund);
        if (!(checkStringEmptiness(fundId) || checkStringEmptiness(fiscalYear))) {
          this.determineNextWarrantNo(
            {
              fund: fundId,
              fiscalYear,
            },
          );
          const assignPaymentToEft = this.warSettingItem
            && this.warSettingItem.assignPaymentNumberToEftWarrants;
          if (this.activeTransItem.status === 'PAID'
            && !(this.activeTransItem.isEftCode && !assignPaymentToEft)) {
            this.determineNextPaymentNo(
              {
                fund: fundId,
                fiscalYear,
              },
            );
          }
        }
      }
    },
    loadFundAndAccBal() {
      const item = (this.editedItem || [])[0];
      if (item && !(this.warSettingItem && this.warSettingItem.multipleFunds)) {
        this.updateStatsBalance(item, warStatsBalanceParams().fundBalance);
        if (!(this.warSettingItem && this.warSettingItem.fundsOnly)) {
          this.updateStatsBalance(item);
        }
      }
    },
    updateStatsBalance(item, balanceType) {
      this.warStatsBalance({
        criteria: {
          fund: extractId(item.fund),
          account: extractId(item.account),
          department: extractId(item.department),
          fiscalYear: this.activeTransItem.fiscalYear,
        },
        params: { balanceType },
      });
    },
    onChangeFund() {
      this.determineNxtTransNos();
      /* trigger parent method for action */
      const { enablePrintWarrant } = (this.warSettingItem || {});
      if (enablePrintWarrant) {
        this.$emit('onChangeFund');
      }
    },
  },
};
</script>
